import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Dialog, Transition } from "@headlessui/react";
import { useLoaderData } from "react-router-dom";
import { createApolloClient } from "../../../providers/ApolloClientFactory";
import { Order } from "../../../types/order";
import {
  DEFAULT_REFUND_REASONS,
  RefundOptionItem,
  RefundOptions,
  RefundOrderResult,
  RefundType,
} from "../../../types/refund";
import { RefundInput, REFUND_ORDER, REFUND_ORDER_V2, RefundOrderInput } from "../mutations";
import { classNames, usdFormat } from "../../../utils";
import Loader from "../../../components/Loader";

// Receipt layout (design B, picked 2026-10-13, RP-2511): one narrow column that
// reads like the printed receipt. Tap a line to take one unit back; the stepper
// then appears inside the line. The confirm button shows the running amount.
// The tablet has the same layout in posapp .../OrderHistory/LineItemRefundModal.js.

// One element of orders.items as the tablet writes it: one unit, no quantity.
// Older payloads may lack the per-unit tax and discount fields; those are
// prorated from the order totals below (legacy mode only; the API does the
// same when it answers).
interface OrderUnit {
  id: number | string;
  name: string;
  price: number;
  item_tax?: number;
  item_discount_applied?: number;
  item_promo_discount_applied?: number;
  item_modifiers_applied?: { id: number; name: string }[];
  modifiers_update?: { id: number; name: string }[];
  is_refunded?: boolean | null;
  refunded_value?: number;
  item_token_value_charged?: number;
  redeemable_token_id?: string;
  redeemable_token_name?: string;
}

// "void": the sale never happened; every open line locked, status voided.
// "full": the goods came back; every open line locked, status refund.
// "partial": the operator picks units, tip and surcharge.
export type RefundMode = "void" | "full" | "partial";

interface LineItemRefundModalProps {
  isOpen: boolean;
  mode?: RefundMode;
  // Answer of getRefundOptions. null = the action is not deployed yet, the
  // modal then computes locally and calls the old insert_refunds_one action.
  options?: RefundOptions | null;
  onClose: () => void;
  onDone?: (result?: RefundOrderResult) => void;
}

const client = createApolloClient();

const toNumber = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const unitsOf = (items: unknown): OrderUnit[] => {
  if (Array.isArray(items)) return items as OrderUnit[];
  const cart = (items as { cartItems?: OrderUnit[] } | null)?.cartItems;
  return Array.isArray(cart) ? cart : [];
};

const modifierIds = (unit: OrderUnit) =>
  (unit.item_modifiers_applied ?? unit.modifiers_update ?? [])
    .map((m) => m.id)
    .sort((a, b) => a - b);

const modifierNames = (unit: OrderUnit) =>
  (unit.item_modifiers_applied ?? unit.modifiers_update ?? [])
    .map((m) => m.name)
    .join(", ");

export const processorLabel = (processor: string | undefined | null) => {
  switch (processor) {
    case "stripe":
      return "Stripe";
    case "mxmerchant":
      return "MX Merchant";
    case "braintree":
      return "Braintree";
    case "closed_loop":
      return "Wristband balance";
    case "cash":
      return "Cash";
    default:
      return processor ?? "Unknown processor";
  }
};

export const processorOf = (order: Order): string | undefined =>
  (order.location as unknown as { payment_processor_config?: { processor?: string } } | undefined)
    ?.payment_processor_config?.processor;

const newIdempotencyKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const copy: Record<RefundMode, { title: string; meaning: string; button: string }> = {
  void: {
    title: "Void order",
    meaning: "This sale never happened. It leaves every sales report, including product mix.",
    button: "Void",
  },
  full: {
    title: "Refund order",
    meaning: "The goods left the vendor. The sale stays in the reports and the full refund is shown against it.",
    button: "Refund",
  },
  partial: {
    title: "Refund",
    meaning: "Some goods came back. The sale stays in the reports and the refund is attributed to the selected items.",
    button: "Refund",
  },
};

const Stepper = ({
  value,
  max,
  disabled,
  name,
  onChange,
}: {
  value: number;
  max: number;
  disabled: boolean;
  name: string;
  onChange: (next: number) => void;
}) => (
  <span className={classNames("inline-flex items-center rounded-md border bg-white", value > 0 ? "border-teal-600" : "border-gray-300")}>
    <button
      type="button"
      className="h-9 w-9 rounded-l-md text-lg leading-none text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-white"
      aria-label={`One fewer ${name}`}
      disabled={disabled || value <= 0}
      onClick={() => onChange(value - 1)}
    >
      −
    </button>
    <output className={classNames("min-w-[2.25rem] text-center text-base font-semibold tabular-nums", value > 0 ? "text-teal-700" : "text-gray-900")}>
      {value}
    </output>
    <button
      type="button"
      className="h-9 w-9 rounded-r-md text-lg leading-none text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-white"
      aria-label={`One more ${name}`}
      disabled={disabled || value >= max}
      onClick={() => onChange(value + 1)}
    >
      +
    </button>
  </span>
);

const Switch = ({ on, label, disabled, onToggle }: { on: boolean; label: string; disabled: boolean; onToggle: () => void }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    disabled={disabled}
    onClick={onToggle}
    className="inline-flex items-center gap-2 font-sans text-sm text-gray-700 disabled:opacity-50"
  >
    <span className={classNames("relative inline-block h-5 w-9 flex-shrink-0 rounded-full transition-colors", on ? "bg-teal-600" : "bg-gray-300")}>
      <span className={classNames("absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all", on ? "left-[18px]" : "left-0.5")} />
    </span>
    <span>{label}</span>
  </button>
);

export const LineItemRefundModal = ({ isOpen, mode = "partial", options = null, onClose, onDone }: LineItemRefundModalProps) => {
  const order = useLoaderData() as Order;
  const locked = mode !== "partial";
  const legacy = !options;
  const [loading, setLoading] = useState(false);
  const [pickedQuantities, setPickedQuantities] = useState<Record<string, number>>({});
  const [pickedTip, setPickedTip] = useState(false);
  const [pickedSurcharge, setPickedSurcharge] = useState(false);
  const [reasonCode, setReasonCode] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const cancelButtonRef = useRef(null);

  // A new key for every opening, so a retry of the same selection replays
  // and a new selection is a new refund.
  useEffect(() => {
    if (isOpen) setIdempotencyKey(newIdempotencyKey());
  }, [isOpen]);

  /* ---------- rows: from the API, or computed locally in legacy mode ---------- */

  const units = useMemo(() => unitsOf(order.items), [order.items]);
  const subtotal = toNumber(order.subtotal);

  const localGroups = useMemo<RefundOptionItem[]>(() => {
    const map = new Map<string, RefundOptionItem>();
    units.forEach((unit) => {
      const price = toNumber(unit.price);
      const share = subtotal > 0 ? price / subtotal : 0;
      const discount =
        unit.item_discount_applied != null || unit.item_promo_discount_applied != null
          ? toNumber(unit.item_discount_applied) + toNumber(unit.item_promo_discount_applied)
          : Math.round((toNumber(order.total_discount) + toNumber(order.total_promo_discount)) * share);
      const tax = unit.item_tax != null ? toNumber(unit.item_tax) : Math.round(toNumber(order.tax) * share);
      const tokenPaid = toNumber(unit.item_token_value_charged);
      const net = Math.max(price - discount - tokenPaid, 0);
      const key = `${unit.id}|${price}|${modifierIds(unit).join("-")}|${tokenPaid > 0 ? "token" : "money"}`;
      const refundable = unit.is_refunded ? 0 : 1;
      const existing = map.get(key);
      if (existing) {
        existing.refundable += refundable;
        existing.total += 1;
        return;
      }
      map.set(key, {
        key,
        item_id: unit.id,
        name: unit.name,
        price,
        modifier_ids: modifierIds(unit),
        modifiers: modifierNames(unit),
        unit_discount: discount,
        unit_net: net,
        unit_tax: tax,
        unit_refund: net + tax,
        paid_with_token: tokenPaid > 0,
        token_name: tokenPaid > 0 ? unit.redeemable_token_name || unit.redeemable_token_id || null : null,
        refundable,
        total: 1,
      });
    });
    return Array.from(map.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, subtotal]);

  const groups = options ? options.items : localGroups;

  const localApproved = (order.transactions ?? []).reduce(
    (sum, t) => sum + (t.payment?.status?.toLowerCase() === "approved" ? toNumber(t.payment.amount) : 0),
    0
  );
  const localRefunded = (order.transactions ?? []).reduce(
    (sum, t) => sum + (t.payment?.status?.toLowerCase() === "refund" ? toNumber(t.payment.amount) : 0),
    0
  );
  const remaining = options ? options.remaining_amount : Math.max(localApproved - localRefunded, 0);

  const tip = options ? options.tip : toNumber(order.tip);
  const surcharge = options ? options.digital_surcharge : toNumber(order.digital_surcharge);
  const tipRefundable = options ? options.tip_refundable : tip > 0;
  const surchargeRefundable = options ? options.digital_surcharge_refundable : surcharge > 0;

  // Locked modes select every open line.
  const quantities: Record<string, number> = locked
    ? Object.fromEntries(groups.map((g) => [g.key, g.refundable]))
    : pickedQuantities;
  const refundTip = locked ? tipRefundable : pickedTip;
  const refundSurcharge = locked ? surchargeRefundable : pickedSurcharge;

  const itemsNet = groups.reduce((sum, g) => sum + (quantities[g.key] ?? 0) * g.unit_net, 0);
  const itemsTax = groups.reduce((sum, g) => sum + (quantities[g.key] ?? 0) * g.unit_tax, 0);
  const linesTotal = itemsNet + itemsTax + (refundTip ? tip : 0) + (refundSurcharge ? surcharge : 0);
  // Token units return the token, not money. A selection of token units only is a refund of $0.
  const tokensPicked = groups.reduce((sum, g) => sum + (g.paid_with_token ? (quantities[g.key] ?? 0) : 0), 0);

  // Every open unit plus the tip and the surcharge is a full refund, not a
  // line-item refund: the server then returns the remaining balance from the
  // payment legs (exact to the cent) and, on MX with the batch open, cancels
  // the charge instead of sending a return.
  const everythingPicked =
    groups.length > 0 &&
    groups.every((g) => (quantities[g.key] ?? 0) === g.refundable) &&
    (!tipRefundable || refundTip) &&
    (!surchargeRefundable || refundSurcharge);
  const effectiveType: RefundType = mode === "void" ? "void" : mode === "full" || everythingPicked ? "full" : "line_item";
  // A full refund or a void returns the remaining balance from the payment
  // legs, not the sum of the lines: prior refunds and rounding can differ by cents.
  const total = effectiveType === "line_item" ? linesTotal : remaining;
  const over = effectiveType === "line_item" && total > remaining;

  /* ---------- processor text ---------- */

  const processor = options ? options.processor : processorOf(order);
  const paymentType = options ? options.tender ?? "" : order.transactions?.[0]?.payment?.payment_type ?? "";
  const isBalanceTender = ["rfid", "cash_balance", "promo_balance", "token"].includes(paymentType.toLowerCase());

  const moneyOutcome = (() => {
    if (effectiveType === "line_item" && total === 0 && tokensPicked > 0) {
      return "The token returns to the wristband. No money moves.";
    }
    if (options) {
      return effectiveType === "line_item" ? options.money_outcome_line_item ?? options.money_outcome : options.money_outcome;
    }
    if (paymentType.toLowerCase() === "cash") return "Cash order. The refund is recorded only. Hand the cash to the customer.";
    switch (processor) {
      case "stripe":
        return "Stripe. The card is refunded for the amount below (5 to 10 business days).";
      case "mxmerchant":
        return "MX Merchant. A referenced return is sent for the amount below (3 to 10 business days).";
      case "braintree":
        return "Braintree. A refund is sent for the amount below.";
      default:
        return "The processor of this location is not known.";
    }
  })();

  const reasons = options?.reasons?.length ? options.reasons : DEFAULT_REFUND_REASONS;
  const settledText =
    options == null
      ? null
      : options.settled === true
        ? `settled (${options.status_before})`
        : options.settled === false
          ? "not settled"
          : "settlement unknown";

  // Why the confirm button is off. Empty = it is on.
  const blocker = (() => {
    if (!order.transactions?.length) return "No transactions available to refund.";
    if (over) {
      return `The selection (${usdFormat.format(total / 100)}) is more than the remaining balance (${usdFormat.format(remaining / 100)}).`;
    }
    if (total <= 0 && (legacy || tokensPicked === 0)) {
      return locked ? "Nothing is left to refund." : "Select at least one item, the tip or the surcharge.";
    }
    if (!legacy && !reasonCode) return "Pick a reason.";
    return "";
  })();
  const canConfirm = !loading && !blocker;

  /* ---------- actions ---------- */

  const summary = () => {
    const parts = groups
      .filter((g) => (quantities[g.key] ?? 0) > 0)
      .map((g) => `${quantities[g.key]}x ${g.name}${g.modifiers ? ` (${g.modifiers})` : ""} ${usdFormat.format(((quantities[g.key] ?? 0) * g.unit_refund) / 100)}`);
    if (refundTip) parts.push(`tip ${usdFormat.format(tip / 100)}`);
    if (refundSurcharge) parts.push(`surcharge ${usdFormat.format(surcharge / 100)}`);
    return parts.join("; ");
  };

  const reset = () => {
    setPickedQuantities({});
    setPickedTip(false);
    setPickedSurcharge(false);
    setReasonCode("");
    setReasonText("");
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const noteText = effectiveType === "line_item" ? `Line-item refund from admin: ${summary()}` : `${copy[mode].title} from admin`;

  const submitNew = async () => {
    const object: RefundOrderInput = {
      order_id: order.id,
      refund_type: effectiveType,
      items:
        effectiveType === "line_item"
          ? groups
              .filter((g) => (quantities[g.key] ?? 0) > 0)
              .map((g) => ({
                item_id: String(g.item_id),
                price: g.price,
                modifier_ids: g.modifier_ids,
                quantity: quantities[g.key],
                paid_with_token: Boolean(g.paid_with_token),
              }))
          : [],
      refund_tip: effectiveType === "line_item" ? refundTip : undefined,
      refund_digital_surcharge: effectiveType === "line_item" ? refundSurcharge : undefined,
      reason_code: reasonCode || null,
      reason: reasonText || null,
      note: noteText,
      idempotency_key: idempotencyKey,
    };
    const { data } = await client.mutate<{ refundOrder: RefundOrderResult }>({
      mutation: REFUND_ORDER_V2,
      variables: { object },
    });
    return data?.refundOrder;
  };

  const submitLegacy = async () => {
    // The old action takes an amount only. The server flags no units, so the
    // selection goes into the transaction note as a trace.
    const object: RefundInput = {
      order_id: order.id,
      amountRefund: total,
      fullRefund: mode === "void",
      paymentType,
      totals: remaining,
      notes: noteText,
    };
    await client.mutate({ mutation: REFUND_ORDER, variables: { object } });
    return undefined;
  };

  const handleConfirm = async () => {
    if (blocker) {
      toast.error(blocker);
      return;
    }
    setLoading(true);
    try {
      const result = legacy ? await submitLegacy() : await submitNew();
      await client.clearStore();
      const amount = result?.amount ?? total;
      toast.success(
        result?.replayed
          ? "This refund was already recorded."
          : `${mode === "void" ? "Voided" : "Refunded"} ${usdFormat.format(amount / 100)}${result?.money_outcome ? `. ${result.money_outcome}` : ""}`
      );
      reset();
      onClose();
      onDone && onDone(result);
    } catch (error) {
      toast.error(`${mode === "void" ? "Void" : "Refund"} failed: ${(error as Error).message}`);
    }
    setLoading(false);
  };

  const setQuantity = (key: string, value: number, max: number) => {
    const next = Math.min(Math.max(Math.floor(value) || 0, 0), max);
    setPickedQuantities((q) => ({ ...q, [key]: next }));
  };

  // "Full refund" at the top: every open unit, the tip and the surcharge.
  // A second press clears the selection.
  const selectEverything = () => {
    if (locked || loading) return;
    if (everythingPicked) {
      setPickedQuantities({});
      setPickedTip(false);
      setPickedSurcharge(false);
      return;
    }
    setPickedQuantities(Object.fromEntries(groups.map((g) => [g.key, g.refundable])));
    setPickedTip(tipRefundable);
    setPickedSurcharge(surchargeRefundable);
  };

  // Tap a line: take one unit back, or put the line back to zero.
  const toggleRow = (g: RefundOptionItem) => {
    if (locked || loading || g.refundable === 0) return;
    setQuantity(g.key, (quantities[g.key] ?? 0) > 0 ? 0 : 1, g.refundable);
  };

  const text = copy[mode];
  const amountLabel = usdFormat.format(total / 100);
  const confirmLabel =
    effectiveType === "line_item" && total === 0 && tokensPicked > 0
      ? `Return ${tokensPicked} token${tokensPicked === 1 ? "" : "s"}`
      : `${text.button} ${amountLabel}`;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-md">
                {/* Header: title, order number, processor state. */}
                <div className="border-b border-dashed border-gray-300 px-5 pt-5 pb-3">
                  <div className="flex items-center justify-between gap-3">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {text.title}
                    </Dialog.Title>
                    {!locked && (
                      <button
                        type="button"
                        aria-pressed={everythingPicked}
                        disabled={loading || groups.length === 0}
                        onClick={selectEverything}
                        className={classNames(
                          "rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50",
                          everythingPicked
                            ? "border-teal-600 bg-teal-600 text-white hover:bg-teal-700"
                            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                        )}
                      >
                        Full refund
                      </button>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap justify-between gap-x-3 font-mono text-xs text-gray-500">
                    <span>#{order.id}</span>
                    <span>
                      {processorLabel(processor)} &middot; {paymentType || "no tender"}
                      {settledText ? ` · ${settledText}` : ""}
                    </span>
                  </div>
                  {locked && <p className="mt-2 text-sm text-gray-700">{text.meaning}</p>}
                  {legacy && (
                    <p className="mt-2 text-xs text-gray-400">Old refund action: the server records the amount only, not the items.</p>
                  )}
                  {legacy && isBalanceTender && (
                    <p className="mt-1 text-xs text-red-600">
                      Wristband tender. The old API refunds only a card leg of a wristband order. A balance order fails until the
                      new action is deployed.
                    </p>
                  )}
                </div>

                {/* Lines: one per item, price and modifiers. Tap to take one back. */}
                <ul className="divide-y divide-gray-100 border-b border-dashed border-gray-300 py-1 font-mono text-sm">
                  {groups.length === 0 && <li className="px-5 py-3 text-gray-500">No items on this order.</li>}
                  {groups.map((g) => {
                    const qty = quantities[g.key] ?? 0;
                    const off = g.refundable === 0;
                    const on = qty > 0;
                    const tappable = !locked && !off && !loading;
                    const detail = [
                      g.modifiers,
                      off ? "refunded earlier" : g.refundable < g.total ? `${g.refundable} of ${g.total} open` : "",
                      g.paid_with_token
                        ? `paid with ${g.token_name ?? "a token"} · the token returns to the wristband`
                        : `${usdFormat.format(g.price / 100)}${g.unit_discount ? ` − ${usdFormat.format(g.unit_discount / 100)}` : ""} + ${usdFormat.format(
                            g.unit_tax / 100
                          )} tax`,
                    ]
                      .filter(Boolean)
                      .join(" · ");
                    return (
                      <li
                        key={g.key}
                        className={classNames(
                          "border-l-4 px-4 py-2.5",
                          on ? "border-teal-600 bg-teal-50" : "border-transparent",
                          off ? "text-gray-400" : "text-gray-900",
                          tappable ? "cursor-pointer" : "",
                          tappable && !on ? "hover:bg-gray-50" : ""
                        )}
                        role={tappable ? "button" : undefined}
                        tabIndex={tappable ? 0 : undefined}
                        aria-pressed={tappable ? on : undefined}
                        onClick={() => toggleRow(g)}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleRow(g);
                          }
                        }}
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="flex min-w-0 items-baseline gap-2">
                            <span className="w-8 flex-shrink-0 text-gray-500">{g.total}×</span>
                            <span className={classNames("truncate", off ? "line-through" : "font-medium")}>{g.name}</span>
                          </div>
                          <span className="flex-shrink-0 font-semibold">{usdFormat.format(g.unit_refund / 100)}</span>
                        </div>
                        <div className="pl-10 font-sans text-xs text-gray-500">{detail}</div>
                        {on && (
                          <div className="mt-1.5 flex items-center justify-between pl-10 font-sans" onClick={(e) => e.stopPropagation()}>
                            <span className="text-xs text-gray-500">
                              {locked ? `${qty} of ${g.total}` : `Take back ${qty} of ${g.refundable}`} &middot;{" "}
                              {usdFormat.format((qty * g.unit_refund) / 100)}
                            </span>
                            {!locked && (
                              <Stepper
                                value={qty}
                                max={g.refundable}
                                disabled={loading}
                                name={g.name}
                                onChange={(next) => setQuantity(g.key, next, g.refundable)}
                              />
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>

                {/* Tip and digital surcharge: all or nothing. */}
                {(tip > 0 || surcharge > 0) && (
                  <div className="border-b border-dashed border-gray-300 py-1 font-mono text-sm text-gray-900">
                    {tip > 0 && (
                      <div className={classNames("flex items-center justify-between px-5 py-2.5", refundTip ? "bg-teal-50" : "")}>
                        <span>
                          Tip
                          {!tipRefundable && <span className="font-sans text-xs text-gray-500"> (already refunded)</span>}
                        </span>
                        <Switch
                          on={refundTip}
                          disabled={locked || !tipRefundable || loading}
                          label={refundTip ? usdFormat.format(tip / 100) : `keep ${usdFormat.format(tip / 100)}`}
                          onToggle={() => setPickedTip(!pickedTip)}
                        />
                      </div>
                    )}
                    {surcharge > 0 && (
                      <div className={classNames("flex items-center justify-between px-5 py-2.5", refundSurcharge ? "bg-teal-50" : "")}>
                        <span>
                          Digital surcharge
                          {!surchargeRefundable && <span className="font-sans text-xs text-gray-500"> (already refunded)</span>}
                        </span>
                        <Switch
                          on={refundSurcharge}
                          disabled={locked || !surchargeRefundable || loading}
                          label={refundSurcharge ? usdFormat.format(surcharge / 100) : `keep ${usdFormat.format(surcharge / 100)}`}
                          onToggle={() => setPickedSurcharge(!pickedSurcharge)}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Reason and note. */}
                <div className="grid grid-cols-[1.25fr_1fr] gap-3 border-b border-dashed border-gray-300 px-5 py-3">
                  <label className="block text-sm">
                    <span className="text-gray-700">Reason{legacy ? "" : " *"}</span>
                    <select
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                      value={reasonCode}
                      disabled={loading}
                      onChange={(e) => setReasonCode(e.target.value)}
                    >
                      <option value="">Pick a reason</option>
                      {reasons.map((r) => (
                        <option key={r.code} value={r.code}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="text-gray-700">Note</span>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                      value={reasonText}
                      maxLength={500}
                      disabled={loading}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder="Optional"
                    />
                  </label>
                </div>

                {/* Sums. */}
                <div className="px-5 pt-3 font-mono text-sm text-gray-900">
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-500">Items</span>
                    <span>{usdFormat.format(itemsNet / 100)}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-500">Tax</span>
                    <span>{usdFormat.format(itemsTax / 100)}</span>
                  </div>
                  {refundTip && (
                    <div className="flex justify-between py-0.5">
                      <span className="text-gray-500">Tip</span>
                      <span>{usdFormat.format(tip / 100)}</span>
                    </div>
                  )}
                  {refundSurcharge && (
                    <div className="flex justify-between py-0.5">
                      <span className="text-gray-500">Surcharge</span>
                      <span>{usdFormat.format(surcharge / 100)}</span>
                    </div>
                  )}
                  <div className="mt-1.5 flex justify-between border-t-2 border-gray-900 pt-2 text-lg font-semibold">
                    <span>{mode === "void" ? "Cancel" : "Refund"}</span>
                    <span>{amountLabel}</span>
                  </div>
                  <div className="flex justify-between py-0.5 text-xs text-gray-500">
                    <span>Remaining after</span>
                    <span>
                      {usdFormat.format(Math.max(remaining - total, 0) / 100)} of {usdFormat.format(remaining / 100)}
                    </span>
                  </div>
                  {effectiveType !== "line_item" && linesTotal !== remaining && (
                    <p className="font-sans text-xs text-gray-500">
                      The lines add up to {usdFormat.format(linesTotal / 100)}. The refund uses the remaining balance of{" "}
                      {usdFormat.format(remaining / 100)} from the payment legs.
                    </p>
                  )}
                </div>

                {/* Money sentence and the one button that moves money. */}
                <div className="grid gap-2 px-5 pt-3 pb-5">
                  {over ? (
                    <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">{blocker}</div>
                  ) : (
                    <p className="text-center text-sm text-gray-600">{moneyOutcome}</p>
                  )}
                  <button
                    type="button"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3.5 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:hover:bg-red-600"
                    onClick={handleConfirm}
                    disabled={!canConfirm}
                  >
                    {loading && <Loader sm />}
                    {confirmLabel}
                  </button>
                  {blocker && !over && <p className="text-center text-xs text-gray-500">{blocker}</p>}
                  <button
                    type="button"
                    ref={cancelButtonRef}
                    className="justify-self-center text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
